import { Expense } from "./interface";

/**
 * Single source of truth for development project-cost math.
 *
 * Why this module exists: the same factor switch was previously duplicated in
 * ConstructionLoanSummary, SecondLienSection and Expenses.tsx. Each copy drifted,
 * so identical data produced different totals on different screens (the Aug 2026
 * "Construction Costs reads $31,500 against a seven-figure budget" report).
 *
 * The other half of that bug was field resolution. The Development model type has
 * TWO `model_type_section_fields` rows with field_key "Gross Buildable Square Feet"
 * — one active, one soft-deleted (active=false) — and the API returns both with no
 * deterministic ordering. A plain
 *   `find(f => f.field_key === "Gross Buildable Square Feet")`
 * therefore returns an arbitrary twin, and when it hit the empty one every
 * "$ / buildable sf" hard-cost row silently multiplied by 0. `resolveFieldValue`
 * below prefers a POPULATED match over the first match for exactly that reason.
 */

/**
 * Names that identify the gross buildable SF assumption, in preference order.
 * Model-type field definitions are per-environment data and have drifted: prod and
 * the dev site use field_key "Gross Buildable Square Feet", the local DB's
 * Development type uses "Gross Square Feet", and one type uses "Buildable SF".
 * Matching a list of candidates keeps the calculation correct in all of them.
 */
export const BUILDABLE_SF_FIELD_NAMES = [
  "Gross Buildable Square Feet",
  "Gross Square Feet",
  "Buildable SF",
] as const;

export const ACQUISITION_PRICE_FIELD_NAMES = ["Acquisition Price"] as const;
export const PREF_MEZZ_FIELD_NAMES = ["Pref. Equity / Mezz. Loan Amount"] as const;

/**
 * Parse a stored field value into a number.
 * Values arrive as Text and may carry thousands separators or currency symbols
 * ("$3,150,000"), which `Number()` alone turns into NaN.
 */
export const parseNumericField = (value: unknown): number => {
  if (value === undefined || value === null || value === "") return 0;
  const n = Number(String(value).replace(/[^0-9.eE+-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const isPopulated = (value: unknown): boolean => parseNumericField(value) > 0;

const nameMatches = (row: any, name: string): boolean =>
  String(row?.field_key ?? "").trim() === name || String(row?.field_title ?? "").trim() === name;

export interface DevelopmentUnitLike {
  avg_sf?: number | string | null;
  units?: number | string | null;
}

/**
 * Find a field value by trying each candidate name, preferring an entry that
 * actually holds a value. Duplicate field_keys make "first match" unsafe.
 * Returns `null` when no entry matches any candidate name at all.
 */
export function resolveFieldValue(
  modelDetails: any,
  names: readonly string[],
): string | number | null {
  const rows = modelDetails?.user_model_field_values;
  if (!Array.isArray(rows)) return null;

  for (const name of names) {
    const populated = rows.find((r: any) => nameMatches(r, name) && isPopulated(r?.value));
    if (populated) return populated.value;
  }
  // Fall back to any match so callers can tell "explicitly zero" from "absent".
  for (const name of names) {
    const anyMatch = rows.find((r: any) => nameMatches(r, name));
    if (anyMatch) return anyMatch.value ?? null;
  }
  return null;
}

/** Numeric convenience wrapper around resolveFieldValue. */
export const resolveNumericField = (modelDetails: any, names: readonly string[]): number =>
  parseNumericField(resolveFieldValue(modelDetails, names));

/**
 * Gross buildable SF, resolved the same way everywhere.
 *
 * Falls back to the development unit mix (avg_sf x units) when the assumption
 * field is missing or empty — the Hard Costs tab has always done this, which is
 * why it kept showing a correct seven-figure total while the loan sections showed
 * a fraction. `developmentUnits` defaults to `modelDetails.development_units`,
 * which CreateModel keeps in sync.
 */
export function resolveBuildableSqFt(
  modelDetails: any,
  developmentUnits?: DevelopmentUnitLike[] | null,
): number {
  const fromField = resolveNumericField(modelDetails, BUILDABLE_SF_FIELD_NAMES);
  if (fromField > 0) return fromField;

  const units = developmentUnits ?? modelDetails?.development_units ?? [];
  const fromUnitMix = (Array.isArray(units) ? units : []).reduce(
    (sum: number, u: DevelopmentUnitLike) =>
      sum + parseNumericField(u?.avg_sf) * parseNumericField(u?.units),
    0,
  );
  if (fromUnitMix > 0) return fromUnitMix;

  // Neither source resolved: every "$ / buildable sf" row will contribute 0, so
  // say so loudly rather than silently rendering a wrong number.
  if (process.env.NODE_ENV !== "test") {
    // eslint-disable-next-line no-console
    console.warn(
      "[projectCostCalcs] Could not resolve gross buildable SF from " +
        `${BUILDABLE_SF_FIELD_NAMES.join(" / ")} or the development unit mix. ` +
        '"$ / buildable sf" costs will read as $0.',
    );
  }
  return 0;
}

export interface CostContext {
  acquisitionPrice: number;
  buildableSqFt: number;
  constructionLoanAmount: number;
  prefMezzAmount: number;
  /** Basis for "percent of hard costs" rows. */
  hardCostsTotal: number;
  allExpenses: Expense[];
}

const CONTINGENCY_FACTOR = "total percent of other expenses";

/** Annual total for a single expense row. */
export function computeRowAnnual(row: Expense, ctx: CostContext): number {
  const costPer = parseNumericField(row?.cost_per);
  const stat = parseNumericField(row?.statistic);
  const factor = String(row?.factor ?? "").trim().toLowerCase();

  if (factor === "total" || factor === "flat") return costPer;
  if (factor === "per unit" || factor === "per sf" || factor === "per month") return costPer * stat;
  if (factor === "$ / buildable sf") return costPer * ctx.buildableSqFt;
  if (factor === "percent of purchase price") return (costPer / 100) * ctx.acquisitionPrice;
  if (factor === "percent of acquisition loan") return 0; // not available in this context
  if (factor === "percent of construction loan") return (costPer / 100) * ctx.constructionLoanAmount;
  if (factor === "percent of pref / mezz loan") return (costPer / 100) * ctx.prefMezzAmount;
  if (factor === "percent of hard costs") return (costPer / 100) * ctx.hardCostsTotal;
  if (factor === CONTINGENCY_FACTOR) {
    // Percent of every other row of the same type, excluding sibling contingency
    // rows so two contingency lines can't compound each other.
    const siblings = (ctx.allExpenses || []).filter(
      (e) =>
        e.type === row.type &&
        e.id !== row.id &&
        String(e.factor ?? "").trim().toLowerCase() !== CONTINGENCY_FACTOR,
    );
    const otherTotal = siblings.reduce(
      (sum, e) => sum + computeRowAnnual(e, { ...ctx, allExpenses: [] }),
      0,
    );
    return (costPer / 100) * otherTotal;
  }
  return 0;
}

/** Sum the annual cost of every expense row of a given type. */
export function sumByType(expenses: Expense[], type: string, ctx: CostContext): number {
  return (expenses || [])
    .filter((e) => e.type === type)
    .reduce((sum, row) => sum + computeRowAnnual(row, ctx), 0);
}

export const COST_TYPES = {
  hard: "Hard Costs",
  closing: "Closing Costs",
  soft: "Soft Costs",
  legalPreDev: "Legal and Pre-Development Costs",
} as const;

export interface CostBreakdown {
  acquisitionPrice: number;
  buildableSqFt: number;
  hardCosts: number;
  closingCosts: number;
  softCosts: number;
  legalPreDevCosts: number;
  interestReserve: number;
  totalProjectCosts: number;
}

/**
 * The full LTC cost stack. Both the Senior Construction Loan and Second Lien
 * summaries call this so their Total Project Costs and LTC can no longer disagree
 * (SecondLienSection previously omitted the interest reserve).
 */
export function computeCostBreakdown(params: {
  expenses: Expense[];
  modelDetails: any;
  developmentUnits?: DevelopmentUnitLike[] | null;
  constructionLoanAmount: number;
  /** Overrides the value read from modelDetails when the caller holds live state. */
  prefMezzAmount?: number;
  interestReserve?: number;
}): CostBreakdown {
  const {
    expenses = [],
    modelDetails,
    developmentUnits,
    constructionLoanAmount,
    prefMezzAmount,
    interestReserve = 0,
  } = params;

  const acquisitionPrice = resolveNumericField(modelDetails, ACQUISITION_PRICE_FIELD_NAMES);
  const buildableSqFt = resolveBuildableSqFt(modelDetails, developmentUnits);
  const resolvedPrefMezz =
    prefMezzAmount !== undefined && prefMezzAmount !== null
      ? prefMezzAmount
      : resolveNumericField(modelDetails, PREF_MEZZ_FIELD_NAMES);

  const base: CostContext = {
    acquisitionPrice,
    buildableSqFt,
    constructionLoanAmount,
    prefMezzAmount: resolvedPrefMezz,
    hardCostsTotal: 0,
    allExpenses: expenses,
  };

  // Hard costs first: they are the basis for "percent of hard costs" rows elsewhere.
  const hardCosts = sumByType(expenses, COST_TYPES.hard, base);
  const ctx: CostContext = { ...base, hardCostsTotal: hardCosts };

  const closingCosts = sumByType(expenses, COST_TYPES.closing, ctx);
  const softCosts = sumByType(expenses, COST_TYPES.soft, ctx);
  const legalPreDevCosts = sumByType(expenses, COST_TYPES.legalPreDev, ctx);

  return {
    acquisitionPrice,
    buildableSqFt,
    hardCosts,
    closingCosts,
    softCosts,
    legalPreDevCosts,
    interestReserve,
    totalProjectCosts:
      acquisitionPrice + hardCosts + closingCosts + softCosts + legalPreDevCosts + interestReserve,
  };
}
